// @flow

import React, { Component } from 'react'
import { connect } from 'react-redux'
import ReactTable from 'react-table'

import {
  fetchBrandsReport,
  getCurrentMetric,
  changeMetric,
  getReport,
  getLoadingState,
} from '../../../stores/report/index'

import LineChart from '../../Charts/LineChart'
import ColorButton from '../../Controls/ColorButton'
import BouncingLoader from '../../Loader/BouncingLoader'
import OpinarySelect from '../../Controls/OpinarySelect'
import OpinaryDatePicker from '../../DatePicker'

import { exportCSVFile, downloadFile } from '../../../util/index'

import moment from 'moment'
import type Moment from 'moment'

type Props = {
  report: Object,
  metric: string,
  isLoading: boolean,
  fetchBrandsReport: Object => void,
  changeMetric: string => void,
}

type State = {
  startDate: Moment,
  endDate: Moment,
}

const headers = ['name', 'impressions', 'votes', 'clicks']

const columns = headers.map(item => ({
  Header: () => (
    <span className="flex">
      <span className="mr2">{item}</span>
      <span className="flex flex-column justify-center">
        <span className="display-sorting-asc chevron-arrow-up" />
        <span className="display-sorting-desc chevron-arrow-down" />
      </span>
    </span>
  ),
  Cell: ({ original }) => (
    <span className="pointer hover-op-con-dark underline-hover">
      {original[item]}
    </span>
  ),
  accessor: item,
  minWidth: 200,
}))

const engagementColum = {
  Header: () => (
    <span className="flex">
      <span className="mr2">engagement rate</span>
      <span className="flex flex-column justify-center">
        <span className="display-sorting-asc chevron-arrow-up" />
        <span className="display-sorting-desc chevron-arrow-down" />
      </span>
    </span>
  ),
  Cell: ({ original }) => (
    <span className="pointer hover-op-con-dark underline-hover">
      {original.engagementrate}%{' '}
    </span>
  ),
  accessor: 'engagementrate',
  minWidth: 200,
}

const clicktTroughColum = {
  Header: () => (
    <span className="flex">
      <span className="mr2">click through rate</span>
      <span className="flex flex-column justify-center">
        <span className="display-sorting-asc chevron-arrow-up" />
        <span className="display-sorting-desc chevron-arrow-down" />
      </span>
    </span>
  ),
  Cell: ({ original }) => (
    <span className="pointer hover-op-con-dark underline-hover">
      {original.clickthroughrate}%{' '}
    </span>
  ),
  accessor: 'clickthroughrate',
  minWidth: 200,
}

const ffclicktTroughColum = {
  Header: () => (
    <span className="flex">
      <span className="mr2">full-funnel click through rate</span>
      <span className="flex flex-column justify-center">
        <span className="display-sorting-asc chevron-arrow-up" />
        <span className="display-sorting-desc chevron-arrow-down" />
      </span>
    </span>
  ),
  Cell: ({ original }) => (
    <span className="pointer hover-op-con-dark underline-hover">
      {original.ffclickthroughrate}%{' '}
    </span>
  ),
  accessor: 'ffclickthroughrate',
  minWidth: 200,
}

const values = [
  { label: 'impression', value: 'impressions' },
  { label: 'votes', value: 'votes' },
  { label: 'clicks', value: 'clicks' },
]

class Report extends Component<Props, State> {
  state = {
    startDate: moment().subtract(7, 'd'),
    endDate: moment().subtract(1, 'd'),
  }

  fetchReport = () =>
    this.props.fetchBrandsReport({
      startDate: this.state.startDate.format('YYYY-MM-DD'),
      endDate: this.state.endDate.format('YYYY-MM-DD'),
    })

  componentDidMount() {
    this.fetchReport()
  }

  handlerSearch = () => this.fetchReport()

  handleChangeStart = (date: Moment) => this.setState({ startDate: date })

  handleChangeEnd = (date: Moment) => this.setState({ endDate: date })

  handlerDowload = report => {
    const blob = exportCSVFile(Object.keys(report.spans[0]), [...report.spans])
    const fileName = `Opinary-report-${Date.now()}.csv`
    downloadFile(blob, fileName)
  }

  render() {
    const { report, isLoading, metric, changeMetric } = this.props

    return !report ? (
      <h1> Loading content </h1>
    ) : (
      <section className="w-100">
        <section className="mb4 flex w-100 tc">
          <div className="bg-op-con-dark white pa4 flex flex-column justify-center w-30">
            <div className="mb3 f4"> UNIQUE IMPRESSIONS </div>
            {isLoading ? (
              <BouncingLoader />
            ) : (
              <div className="f1 fw7"> {report.total.impressions} </div>
            )}
          </div>

          <div className="bg-op-con-dark white ml-auto mr-auto pa4 flex flex-column justify-center w-30">
            <div className="mb3 f4"> UNIQUE VOTES </div>
            {isLoading ? (
              <BouncingLoader />
            ) : (
              <div className="f1 fw7"> {report.total.votes} </div>
            )}
          </div>

          <div className="bg-op-con-dark white pa4 flex flex-column justify-center w-30">
            <div className="mb3 f4"> CLICKS </div>
            {isLoading ? (
              <BouncingLoader />
            ) : (
              <div className="f1 fw7"> {report.total.clicks} </div>
            )}
          </div>

          <div className="bg-op-con-dark white pa4 flex flex-column justify-center w-30">
            <div className="mb3 f4"> ENGAGEMENT RATE </div>
            {isLoading ? (
              <BouncingLoader />
            ) : (
              <div className="f1 fw7">
                {' '}
                {(
                  (report.total.votes / report.total.impressions) * 100 || 0
                ).toFixed(2)}{' '}
                %{' '}
              </div>
            )}
          </div>

          <div className="bg-op-con-dark white ml-auto mr-auto pa4 flex flex-column justify-center w-15">
            <div className="mb3 f4"> CTR </div>
            {isLoading ? (
              <BouncingLoader />
            ) : (
              <div className="f1 fw7">
                {' '}
                {(
                  (report.total.clicks / report.total.votes) * 100 || 0
                ).toFixed(2)}{' '}
                %{' '}
              </div>
            )}
          </div>

          <div className="bg-op-con-dark white pa4 flex flex-column justify-center w-15">
            <div className="mb3 f4"> Full-funnel CTR </div>
            {isLoading ? (
              <BouncingLoader />
            ) : (
              <div className="f1 fw7">
                {' '}
                {(
                  (report.total.clicks / report.total.impressions) * 100 || 0
                ).toFixed(2)}{' '}
                %{' '}
              </div>
            )}
          </div>
        </section>

        <div className="w-100 pl4 h3 bg-near-white flex  items-center">
          <span className="pr2"> Start date:</span>
          <OpinaryDatePicker
            className="h2 bb b--gray w-50 bg-transparent dark-gray flex-auto"
            selected={this.state.startDate}
            selectsStart
            startDate={this.state.startDate}
            endDate={this.state.endDate}
            onChange={this.handleChangeStart}
            showToolTip
          />

          <span className="pr2">End date:</span>
          <OpinaryDatePicker
            className="h2 bb b--gray w-50 bg-transparent dark-gray flex-auto"
            selected={this.state.endDate}
            selectsEnd
            startDate={this.state.startDate}
            endDate={this.state.endDate}
            onChange={this.handleChangeEnd}
          />

          <ColorButton
            className="z-3"
            colorClass="op-brand"
            borderClass="b--op-brand"
            onClick={this.handlerSearch}
          >
            {' '}
            Select Date{' '}
          </ColorButton>

          <ColorButton
            className="ml4"
            colorClass="gray"
            borderClass="b--gray"
            onClick={() => this.handlerDowload(report)}
          >
            {' '}
            Export to CSV{' '}
          </ColorButton>
        </div>

        <section className="bg-white">
          <ReactTable
            columns={[
              ...columns,
              engagementColum,
              clicktTroughColum,
              ffclicktTroughColum,
            ]}
            data={report.polls}
            defaultPageSize={10}
            defaultSortDesc={true}
            getTrProps={() => ({ className: 'flex' })}
            getTheadTrProps={() => ({ className: 'flex' })}
            defaultSorted={[{ id: 'impressions', desc: true }]}
            getTheadThProps={() => ({
              className: 'bg-op-con-dark bn white ws-normal tl pv2 f6',
            })}
            getTdProps={() => ({
              className: 'gray b--moon-gray bt pv2 flex items-center f6',
              style: { minHeight: '3rem' },
            })}
          />
        </section>
        <br />
        <br />
        <section className="mb4 flex w-100 tc">
          <div className="bg-white ml-auto mr-auto pa4 flex flex-column justify-center w-100">
            <div className="flex  items-center ">
              <h4>Metric:</h4>
              <OpinarySelect
                className="w-10"
                value={metric}
                options={values}
                onChange={changeMetric}
              />
            </div>
            <div className="mb3 f4"> {metric.toUpperCase()} </div>
            {isLoading ? (
              <BouncingLoader color="#000" />
            ) : (
              <LineChart graphData={report.spans} title={metric} k={metric} />
            )}
          </div>
        </section>

        <section className="mb4 flex w-100 tc">
          <div className="bg-white ml-auto mr-auto pa4 flex flex-column justify-center w-33">
            <div className="mb3 f4"> ENGAGEMENT RATE % </div>
            {isLoading ? (
              <BouncingLoader color="#000" />
            ) : (
              <LineChart
                graphData={report.spans}
                title="% engagement"
                k={'engagementrate'}
              />
            )}
          </div>

          <div className="bg-white ml-auto mr-auto pa4 flex flex-column justify-center w-33">
            <div className="mb3 f4"> CLICK THROUGH RATE % </div>
            {isLoading ? (
              <BouncingLoader color="#000" />
            ) : (
              <LineChart
                graphData={report.spans}
                title="% clicks"
                k={'clickthroughrate'}
              />
            )}
          </div>

          <div className="bg-white ml-auto mr-auto pa4 flex flex-column justify-center w-33">
            <div className="mb3 f4"> FULL-FUNNEL CLICK THROUGH RATE % </div>
            {isLoading ? (
              <BouncingLoader color="#000" />
            ) : (
              <LineChart
                graphData={report.spans}
                title="% clicks"
                k={'ffclickthroughrate'}
              />
            )}
          </div>
        </section>
      </section>
    )
  }
}

const mapStateToProps = state => ({
  report: getReport(state),
  isLoading: getLoadingState(state),
  metric: getCurrentMetric(state),
})

const mapDispatchToProps = {
  fetchBrandsReport,
  changeMetric,
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(Report)



// WEBPACK FOOTER //
// ./src/components/Pages/Report/index.js